import { useState, useEffect, useMemo, useCallback } from 'react'
import { Link } from 'react-router-dom'
import Editor, { type Monaco } from '@monaco-editor/react'
import Logo from '../components/Logo'
import AuthHeader from '../components/AuthHeader'
import LeanSnippet from '../components/LeanSnippet'
import { resourceFormalApi, type RFSource } from '../utils/api'

// ============================================================================
// Variant catalogue — each variant lists the layers it walks through and
// where the corresponding Lean source lives.
// ============================================================================

interface LayerSpec {
  id: string
  num: string                                 // "L1" / "L2" / ... / "Sys"
  title: string
  subtitle: string
  description: string
  path: string                                // .lean file
  decl: string                                // declaration name to slice
  caption?: string                            // header for the snippet
  highlights?: string[]                       // bullet-list takeaways
  diagram: 'spec' | 'logical' | 'ppm' | 'qec' | 'syslayer'
}

interface Variant {
  id: string
  name: string
  badge: string
  oneLine: string
  layers: LayerSpec[]
  available: boolean
}

const VARIANTS: Variant[] = [
  {
    id: 'halfadder-1bit',
    name: '1-bit half adder',
    badge: 'Worked example',
    oneLine: 'b ↦ a ⊕ b, c ↦ a ∧ b — the smallest non-trivial reversible adder. End-to-end specified, scheduled, coded, and architected.',
    available: true,
    layers: [
      {
        id: 'l1',
        num: 'L1',
        title: 'Specification',
        subtitle: 'What the circuit must compute',
        description:
          'The function-spec registry pins the canonical gate-kind sequence the framework will accept. ' +
          'Any LogicalImpl claiming to compute half_adder_1bit must match this sequence.',
        path: 'lean/QianxuAudit/Framework/AuditPipeline.lean',
        decl: 'spec_half_adder_1bit',
        caption: 'spec_half_adder_1bit',
        diagram: 'spec',
        highlights: [
          'LCCX(0,1,2) — Toffoli computes carry = a ∧ b',
          'LCNOT(0,1) — CNOT computes sum = a ⊕ b',
          'Lives in the trusted function-spec registry — the implementer cannot smuggle in its own spec',
        ],
      },
      {
        id: 'l2',
        num: 'L2',
        title: 'Logical schedule',
        subtitle: 'Abstract qubits, time-ordered',
        description:
          'The implementer writes the logical-level program: two gate instances with begin/end timestamps. ' +
          'No physical detail yet — only the WHAT, not the HOW.',
        path: 'lean/QianxuAudit/Corpus/TwoLevelHalfAdder.lean',
        decl: 'hba_logical',
        caption: 'hba_logical : LogicalSchedule',
        diagram: 'logical',
        highlights: [
          'Two LogicalStep entries: Lccx then Lcnot',
          'Timestamps drive scheduling but stay abstract over hardware',
          'logical_verifier checks: non-empty, time-ordered',
        ],
      },
      {
        id: 'l3',
        num: 'L3',
        title: 'PPM / Magic-state realisation',
        subtitle: 'How LCCX decomposes',
        description:
          'The physical schedule decomposes each logical gate. A Toffoli (LCCX) becomes magic-state ' +
          'injection: request a |CCZ⟩ state, route it in, fuse via 3 CNOTs, measure 3 ancillae, apply Pauli-frame ' +
          'correction. Every step is an explicit SysCall.',
        path: 'lean/QianxuAudit/Corpus/TwoLevelHalfAdder.lean',
        decl: 'hba_physical',
        caption: 'hba_physical : Schedule  (the 18 explicit SysCalls)',
        diagram: 'ppm',
        highlights: [
          'Phase B: magicReq blocks for 12 ms — the |CCZ⟩ production cost from ccz_spec_qianxu',
          'Phase D: 3 CNOTs are the teleportation fusion',
          'Phase F: pauliUpdate is the classical S-correction — explicit, not implicit',
          'Phases A/C/H: TransitQubit moves atoms between zones; every move costs latency',
        ],
      },
      {
        id: 'l4',
        num: 'L4',
        title: 'QEC code layout',
        subtitle: 'Logical → code block',
        description:
          'Each logical qubit is backed by a code block — here LP[[144, 18, 12]] bivariate-bicycle ' +
          'qLDPC. The CodedLogicalLayout pins each flat logical-qubit ID to a (block_id, local_index) pair ' +
          'within the block; consistency is decidable.',
        path: 'lean/QianxuAudit/Corpus/LPCodedAdderDemo.lean',
        decl: 'adder_coded_layout',
        caption: 'adder_coded_layout : CodedLogicalLayout',
        diagram: 'qec',
        highlights: [
          '[[n=144, k=18, d=12]] — 144 physical atoms encode 18 logical qubits with distance 12',
          'ℓ_a → (block 0, local 3) · ℓ_b → (block 0, local 15) · ℓ_c → (block 0, local 17)',
          'consistent predicate: blocks have correct size + indices in range + every gate target bound',
        ],
      },
      {
        id: 'sys',
        num: 'Sys',
        title: 'Architecture & SysCalls',
        subtitle: 'Zoned hardware substrate',
        description:
          'The schedule executes on a zoned architecture with explicit decoder and Pauli-frame syscalls. ' +
          'Zones tag patches with roles (Memory / Processor / Ancilla / Factory); channels are buses with ' +
          'bandwidth, latency, and fidelity. The same predicate verifies on neutral-atom, superconducting, ' +
          'and trapped-ion platforms.',
        path: 'lean/QianxuAudit/Corpus/TwoLevelHalfAdder.lean',
        decl: 'hba_arch',
        caption: 'hba_arch : Architecture',
        diagram: 'syslayer',
        highlights: [
          'Factory zone capacity = ccz_spec_qianxu.factory_qubits — paper-cited footprint',
          't_stab_cycle_us = 1000 µs · t_react_us = 100 µs (decoder reaction budget)',
          'Four channels: MemoryLoad / MemorySave / AncillaSupply / MagicSupply',
        ],
      },
    ],
  },
  {
    id: 'halfadder-2bit',
    name: '2-bit independent half-adders',
    badge: 'Multi-Toffoli',
    oneLine: 'Two parallel half-adders on (a₀,b₀,c₀) and (a₁,b₁,c₁). Doubles the magic-state demand to stress the factory throughput invariant.',
    available: false,
    layers: [],
  },
  {
    id: 'cuccaro-2bit',
    name: 'Cuccaro ripple-carry (2-bit)',
    badge: 'Carry chain',
    oneLine: 'Full ripple-carry: 4 Toffolis + 9 CNOTs. Demonstrates how the carry-out CX bridges per-bit Toffolis. Coming next.',
    available: false,
    layers: [],
  },
]

// ============================================================================
// Source loading
// ============================================================================

interface SourceMap {
  [path: string]: RFSource | null  // null = loading; missing key = not requested
}

function sliceDecl(source: RFSource | null | undefined, declName: string): { code: string; line?: number; endLine?: number } {
  if (!source) return { code: '// loading…' }
  const decl = source.declarations.find(d => d.name === declName)
  if (!decl) return { code: `-- declaration ${declName} not found in ${source.path}` }
  const lines = source.content.split('\n')
  const start = Math.max(0, decl.line - 1)
  const end = Math.min(lines.length, (decl.end_line ?? decl.line) + 0)
  // Also pull a few preceding comment lines if they look like a docstring.
  let docStart = start
  let k = start - 1
  while (k >= 0 && lines[k].trim() === '') k--
  if (k >= 0 && lines[k].trimEnd().endsWith('-/')) {
    while (k >= 0 && !lines[k].trimStart().startsWith('/-')) k--
    if (k >= 0) docStart = k
  }
  return { code: lines.slice(docStart, end).join('\n').trimEnd(), line: docStart + 1, endLine: end }
}

// ============================================================================
// Per-layer SVG diagrams — small, schematic
// ============================================================================

function SpecDiagram() {
  // half-adder circuit diagram: 3 wires (a, b, c), CCX then CNOT
  return (
    <svg viewBox="0 0 360 150" className="w-full max-w-md">
      <g fontFamily="ui-monospace, monospace" fontSize={11}>
        {/* Wires */}
        {[40, 80, 120].map((y, i) => (
          <g key={i}>
            <line x1={40} y1={y} x2={340} y2={y} stroke="#475569" strokeWidth={1.2} />
            <text x={12} y={y + 4} fill="#0f172a" fontWeight={700}>{['a', 'b', 'c'][i]}</text>
            <text x={342} y={y + 4} fill="#475569">|{['a', 'a⊕b', 'a∧b'][i]}⟩</text>
          </g>
        ))}
        {/* CCX (control-control-target on a=ctrl, b=ctrl, c=tgt) */}
        <g>
          <line x1={150} y1={40} x2={150} y2={120} stroke="#6366f1" strokeWidth={1.6} />
          <circle cx={150} cy={40} r={4} fill="#6366f1" />
          <circle cx={150} cy={80} r={4} fill="#6366f1" />
          <circle cx={150} cy={120} r={9} fill="none" stroke="#6366f1" strokeWidth={1.6} />
          <line x1={141} y1={120} x2={159} y2={120} stroke="#6366f1" strokeWidth={1.4} />
          <line x1={150} y1={111} x2={150} y2={129} stroke="#6366f1" strokeWidth={1.4} />
          <text x={150} y={145} textAnchor="middle" fontSize={10} fill="#6366f1" fontWeight={700}>LCCX</text>
        </g>
        {/* CNOT (control a, target b) */}
        <g>
          <line x1={240} y1={40} x2={240} y2={80} stroke="#f43f5e" strokeWidth={1.6} />
          <circle cx={240} cy={40} r={4} fill="#f43f5e" />
          <circle cx={240} cy={80} r={9} fill="none" stroke="#f43f5e" strokeWidth={1.6} />
          <line x1={231} y1={80} x2={249} y2={80} stroke="#f43f5e" strokeWidth={1.4} />
          <line x1={240} y1={71} x2={240} y2={89} stroke="#f43f5e" strokeWidth={1.4} />
          <text x={240} y={105} textAnchor="middle" fontSize={10} fill="#f43f5e" fontWeight={700}>LCNOT</text>
        </g>
      </g>
    </svg>
  )
}

function LogicalDiagram() {
  // Timeline of 2 logical gates with begin/end timestamps
  return (
    <svg viewBox="0 0 360 160" className="w-full max-w-md">
      <g fontFamily="ui-monospace, monospace" fontSize={10}>
        {/* Time axis */}
        <line x1={40} y1={130} x2={340} y2={130} stroke="#cbd5e1" strokeWidth={1} />
        <text x={40} y={148} fontSize={9} fill="#64748b" textAnchor="middle">12090 µs</text>
        <text x={250} y={148} fontSize={9} fill="#64748b" textAnchor="middle">12097</text>
        <text x={310} y={148} fontSize={9} fill="#64748b" textAnchor="middle">12098</text>
        {/* Gate blocks */}
        <g>
          <rect x={40} y={45} width={210} height={32} rx={4} fill="#eef2ff" stroke="#6366f1" strokeWidth={1.4} />
          <text x={145} y={64} textAnchor="middle" fontSize={11} fontWeight={700} fill="#3730a3">LCCX(a, b, c)</text>
          <text x={145} y={88} textAnchor="middle" fontSize={9} fill="#475569">id=0 · 7 µs</text>
        </g>
        <g>
          <rect x={250} y={45} width={60} height={32} rx={4} fill="#fff1f2" stroke="#f43f5e" strokeWidth={1.4} />
          <text x={280} y={64} textAnchor="middle" fontSize={11} fontWeight={700} fill="#9f1239">LCNOT</text>
          <text x={280} y={88} textAnchor="middle" fontSize={9} fill="#475569">id=1 · 1 µs</text>
        </g>
        <text x={20} y={20} fontSize={11} fontWeight={700} fill="#0f172a">Logical timeline</text>
      </g>
    </svg>
  )
}

function PPMDiagram() {
  const phases = [
    { label: 'A · transit (a,b,c → Proc)', x: 5, w: 12, color: '#6366f1' },
    { label: 'B · magicReq |CCZ⟩', x: 18, w: 38, color: '#f59e0b' },
    { label: 'C · transit (magic → Proc)', x: 56, w: 12, color: '#6366f1' },
    { label: 'D · 3× CNOT (fuse)', x: 68, w: 6, color: '#ec4899' },
    { label: 'E · 3× measZ', x: 74, w: 6, color: '#0ea5e9' },
    { label: 'F · pauliUpdate', x: 80, w: 3, color: '#a855f7' },
    { label: 'G · cnot(a,b)', x: 83, w: 3, color: '#f43f5e' },
    { label: 'H · transit (save → Mem)', x: 86, w: 9, color: '#6366f1' },
  ]
  return (
    <svg viewBox="0 0 360 200" className="w-full max-w-md">
      <g fontFamily="ui-monospace, monospace" fontSize={9}>
        {phases.map((p, i) => (
          <g key={i}>
            <rect
              x={5 + (p.x / 100) * 340}
              y={20 + i * 20}
              width={(p.w / 100) * 340}
              height={14}
              rx={2.5}
              fill={p.color}
              opacity={0.85}
            />
            <text x={5 + (p.x / 100) * 340 - 4} y={20 + i * 20 + 11} textAnchor="end" fill="#0f172a" fontWeight={600}>
              {p.label}
            </text>
          </g>
        ))}
      </g>
    </svg>
  )
}

function QECDiagram() {
  // [[144,18,12]] block showing 3 highlighted local indices among 144
  const highlighted = new Set([3, 15, 17])
  return (
    <svg viewBox="0 0 360 220" className="w-full max-w-md">
      <g fontFamily="ui-monospace, monospace">
        <text x={10} y={18} fontSize={11} fontWeight={700} fill="#0f172a">LP[[144, 18, 12]] block — 144 physical atoms</text>
        <text x={10} y={32} fontSize={9} fill="#64748b">3 highlighted = the half-adder's 3 logical qubits</text>
        {/* 12x12 grid of dots */}
        {Array.from({ length: 144 }).map((_, idx) => {
          const r = Math.floor(idx / 12), c = idx % 12
          const x = 30 + c * 24, y = 50 + r * 12
          const hi = highlighted.has(idx)
          return (
            <g key={idx}>
              <circle cx={x} cy={y} r={hi ? 4 : 2} fill={hi ? '#f43f5e' : '#cbd5e1'} stroke={hi ? '#9f1239' : 'none'} strokeWidth={hi ? 1 : 0} />
              {hi && (
                <text x={x + 7} y={y + 3} fontSize={9} fill="#9f1239" fontWeight={700}>
                  {idx === 3 ? 'ℓ_a' : idx === 15 ? 'ℓ_b' : 'ℓ_c'}
                </text>
              )}
            </g>
          )
        })}
      </g>
    </svg>
  )
}

function SysLayerDiagram() {
  // 4 zones with channels between them
  const zones = [
    { id: 0, name: 'Memory',    role: 'data',    x: 40,  y: 50,  cap: 4, color: '#10b981' },
    { id: 1, name: 'Processor', role: 'compute', x: 180, y: 50,  cap: 6, color: '#f43f5e' },
    { id: 2, name: 'Ancilla',   role: 'helper',  x: 40,  y: 145, cap: 4, color: '#0ea5e9' },
    { id: 3, name: 'Factory',   role: '|CCZ⟩',   x: 280, y: 145, cap: 14, color: '#f59e0b' },
  ]
  return (
    <svg viewBox="0 0 380 220" className="w-full max-w-md">
      <defs>
        <marker id="arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto">
          <path d="M0 0 L10 5 L0 10 z" fill="#94a3b8" />
        </marker>
      </defs>
      <g fontFamily="ui-monospace, monospace">
        {zones.map(z => (
          <g key={z.id}>
            <rect x={z.x - 50} y={z.y - 28} width={100} height={56} rx={8} fill="white" stroke={z.color} strokeWidth={1.8} />
            <text x={z.x} y={z.y - 8} textAnchor="middle" fontSize={11} fontWeight={700} fill="#0f172a">{z.name}</text>
            <text x={z.x} y={z.y + 6} textAnchor="middle" fontSize={9} fill={z.color}>{z.role}</text>
            <text x={z.x} y={z.y + 20} textAnchor="middle" fontSize={9} fill="#64748b">cap: {z.cap}</text>
          </g>
        ))}
        {/* Channels */}
        {[
          { from: [40, 80], to: [180, 80], label: 'MemoryLoad / MemorySave' },
          { from: [40, 145], to: [130, 70], label: 'AncillaSupply' },
          { from: [280, 117], to: [230, 70], label: 'MagicSupply (12ms)' },
        ].map((c, i) => (
          <g key={i}>
            <line x1={c.from[0]} y1={c.from[1]} x2={c.to[0]} y2={c.to[1]} stroke="#94a3b8" strokeWidth={1.2} markerEnd="url(#arrow)" />
            <text x={(c.from[0] + c.to[0]) / 2} y={(c.from[1] + c.to[1]) / 2 - 4} textAnchor="middle" fontSize={9} fill="#64748b">
              {c.label}
            </text>
          </g>
        ))}
      </g>
    </svg>
  )
}

const DIAGRAMS: Record<string, () => JSX.Element> = {
  spec: SpecDiagram,
  logical: LogicalDiagram,
  ppm: PPMDiagram,
  qec: QECDiagram,
  syslayer: SysLayerDiagram,
}

const LAYER_ACCENT: Record<string, string> = {
  L1: 'border-rose-200 from-rose-50',
  L2: 'border-amber-200 from-amber-50',
  L3: 'border-emerald-200 from-emerald-50',
  L4: 'border-sky-200 from-sky-50',
  Sys: 'border-indigo-200 from-indigo-50',
}

// ============================================================================
// Page
// ============================================================================

function registerLean4(monaco: Monaco) {
  if (monaco.languages.getLanguages().some((l: { id: string }) => l.id === 'lean4')) return
  monaco.languages.register({ id: 'lean4', extensions: ['.lean'] })
  monaco.languages.setMonarchTokensProvider('lean4', {
    defaultToken: '',
    keywords: ['theorem', 'def', 'abbrev', 'structure', 'inductive', 'example', 'axiom', 'by', 'sorry', 'decide', 'rfl', 'import', 'namespace', 'open', 'end'],
    tokenizer: {
      root: [
        [/\/-/, 'comment', '@blockComment'],
        [/--.*$/, 'comment'],
        [/\b(theorem|def|abbrev|structure|inductive|example|axiom)\b/, 'keyword.declaration'],
        [/\b(by|sorry|decide|rfl|simp|exact)\b/, 'keyword'],
        [/\b(import|namespace|open|end)\b/, 'keyword.module'],
        [/[A-Z][\w']*/, 'type'],
        [/"([^"\\]|\\.)*"/, 'string'],
        [/\d+/, 'number'],
      ],
      blockComment: [[/-\//, 'comment', '@pop'], [/[^-]+/, 'comment'], [/-/, 'comment']],
    },
  })
}

function TutorialAdderPage() {
  const [variantId, setVariantId] = useState<string>(VARIANTS[0].id)
  const [sources, setSources] = useState<SourceMap>({})
  const [openFile, setOpenFile] = useState<string | null>(null)
  const [openLine, setOpenLine] = useState<number | null>(null)

  const variant = useMemo(() => VARIANTS.find(v => v.id === variantId)!, [variantId])

  // Fetch unique source files for the active variant.
  useEffect(() => {
    const needed = Array.from(new Set(variant.layers.map(l => l.path)))
    needed.forEach(path => {
      if (sources[path] !== undefined) return
      setSources(s => ({ ...s, [path]: null }))
      resourceFormalApi
        .getSource(path)
        .then(src => setSources(s => ({ ...s, [path]: src })))
        .catch(() => setSources(s => ({ ...s, [path]: null })))
    })
  }, [variant, sources])

  const onOpenFile = useCallback((path: string, line?: number) => {
    setOpenFile(path)
    setOpenLine(line ?? null)
  }, [])

  return (
    <div className="min-h-screen bg-gradient-to-br from-qcloud-light via-white to-qcloud-bg">
      <header className="bg-white/85 backdrop-blur border-b border-qcloud-border px-6 py-3 flex items-center justify-between sticky top-0 z-20">
        <div className="flex items-center gap-3">
          <Link to="/" className="flex items-center gap-2 hover:opacity-80">
            <Logo size="small" />
            <span className="text-xl font-bold bg-gradient-to-r from-qcloud-primary to-qcloud-secondary bg-clip-text text-transparent">QuantumArena</span>
          </Link>
          <span className="text-qcloud-border">·</span>
          <Link to="/resource-verification" className="text-sm text-qcloud-muted hover:text-qcloud-primary transition-colors">Resource Verification</Link>
          <span className="text-qcloud-border">›</span>
          <span className="text-sm font-semibold text-qcloud-text">Tutorial · Adder</span>
        </div>
        <AuthHeader />
      </header>

      <section className="max-w-6xl mx-auto px-6 pt-10 pb-2">
        <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest font-bold text-qcloud-primary bg-qcloud-primary/10 rounded-full px-3 py-1 mb-3">
          <span className="w-1.5 h-1.5 rounded-full bg-qcloud-primary animate-pulse" />
          Worked example
        </div>
        <h1 className="text-3xl md:text-4xl font-bold text-qcloud-text mb-2 leading-tight">
          Adder, layer by layer
        </h1>
        <p className="text-base text-qcloud-muted max-w-3xl">
          One concrete circuit, walked from abstract specification down to atom moves on neutral-atom hardware.
          Each layer shows the actual Lean program plus a schematic of what it does.
        </p>
      </section>

      {/* Variant tabs */}
      <section className="max-w-6xl mx-auto px-6 pt-4 pb-2">
        <div className="flex flex-wrap gap-2">
          {VARIANTS.map(v => {
            const active = v.id === variantId
            return (
              <button
                key={v.id}
                onClick={() => v.available && setVariantId(v.id)}
                disabled={!v.available}
                className={`px-3 py-2 rounded-lg border text-left transition-colors ${
                  active
                    ? 'border-qcloud-primary bg-qcloud-primary/5 text-qcloud-primary'
                    : v.available
                      ? 'border-qcloud-border hover:border-qcloud-primary/50 text-qcloud-text'
                      : 'border-slate-200 text-slate-400 cursor-not-allowed bg-slate-50'
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-sm">{v.name}</span>
                  <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{v.badge}</span>
                  {!v.available && <span className="text-[9px] uppercase tracking-wider text-slate-400">soon</span>}
                </div>
              </button>
            )
          })}
        </div>
        <p className="text-sm text-qcloud-muted mt-3 italic">{variant.oneLine}</p>
      </section>

      {/* Layer stack */}
      <main className="max-w-6xl mx-auto px-6 pb-12 mt-6 space-y-5">
        {variant.layers.length === 0 && (
          <div className="bg-white rounded-2xl border border-qcloud-border p-10 text-center text-qcloud-muted">
            This variant's walkthrough is coming next.
          </div>
        )}
        {variant.layers.map((layer, i) => {
          const src = sources[layer.path]
          const slice = sliceDecl(src, layer.decl)
          const accent = LAYER_ACCENT[layer.num] || 'border-slate-200 from-slate-50'
          const Diagram = DIAGRAMS[layer.diagram]
          return (
            <section
              key={layer.id}
              className={`rounded-2xl border-2 ${accent.split(' ')[0]} bg-gradient-to-br ${accent.split(' ')[1]} to-white p-6`}
            >
              <div className="flex items-start gap-4 mb-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-white border-2 border-current text-qcloud-text flex items-center justify-center font-mono font-bold text-sm">
                  {layer.num}
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-xl font-bold text-qcloud-text">{layer.title}</h2>
                  <div className="text-xs uppercase tracking-wider text-qcloud-muted font-semibold mt-0.5">{layer.subtitle}</div>
                  <p className="text-sm text-qcloud-muted mt-2 leading-relaxed">{layer.description}</p>
                </div>
                <div className="hidden md:block text-[10px] font-mono text-qcloud-muted opacity-60">step {i + 1} / {variant.layers.length}</div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                {/* Lean snippet (60%) */}
                <div className="lg:col-span-7">
                  <LeanSnippet
                    code={slice.code}
                    filePath={layer.path}
                    startLine={slice.line}
                    caption={layer.caption}
                    maxHeight="380px"
                    fullFileLink={{ path: layer.path, line: slice.line, onClick: onOpenFile }}
                  />
                </div>

                {/* Diagram + highlights (40%) */}
                <div className="lg:col-span-5 space-y-3">
                  {Diagram && (
                    <div className="bg-white rounded-lg border border-slate-200 p-3 flex items-center justify-center">
                      <Diagram />
                    </div>
                  )}
                  {layer.highlights && (
                    <ul className="space-y-1 text-[12.5px] text-qcloud-text">
                      {layer.highlights.map((h, j) => (
                        <li key={j} className="flex items-start gap-2">
                          <span className="text-qcloud-primary mt-1">▸</span>
                          <span className="leading-snug">{h}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </section>
          )
        })}

        {/* Wrap-up callout */}
        {variant.layers.length > 0 && (
          <section className="rounded-2xl border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-white p-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-purple-100 text-purple-700 flex items-center justify-center font-bold text-base">
                ✓
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-bold text-qcloud-text">All five layers compose</h2>
                <p className="text-sm text-qcloud-muted mt-1 mb-3 leading-relaxed">
                  The framework's <code className="font-mono text-xs">submission_verified</code> theorem chains the three
                  independent verifiers (logical, physical, cross-level) plus the semantic check across all five layers.
                  For the 1-bit half-adder, every check passes by <code className="font-mono text-xs">decide</code> at
                  the kernel level — no axioms, no <code className="font-mono text-xs">sorry</code>.
                </p>
                <div className="flex flex-wrap gap-2 text-[11px]">
                  <Link to="/resource-verification" className="px-3 py-1.5 rounded-lg bg-purple-100 text-purple-800 font-semibold hover:bg-purple-200 transition-colors">
                    See the framework diagram →
                  </Link>
                  <Link to="/resource-verification/graph" className="px-3 py-1.5 rounded-lg bg-white border border-purple-200 text-purple-700 font-semibold hover:border-purple-400 transition-colors">
                    See the dependency graph →
                  </Link>
                  <button
                    onClick={() => onOpenFile('lean/QianxuAudit/Corpus/HalfAdderSubmission.lean')}
                    className="px-3 py-1.5 rounded-lg bg-white border border-purple-200 text-purple-700 font-semibold hover:border-purple-400 transition-colors"
                  >
                    Open the headline theorem →
                  </button>
                </div>
              </div>
            </div>
          </section>
        )}
      </main>

      {/* Full-file slide-over */}
      {openFile && (
        <FullFileSlideOver
          path={openFile}
          line={openLine}
          onClose={() => { setOpenFile(null); setOpenLine(null) }}
        />
      )}
    </div>
  )
}

function FullFileSlideOver({ path, line, onClose }: { path: string, line: number | null, onClose: () => void }) {
  const [source, setSource] = useState<RFSource | null>(null)
  useEffect(() => {
    setSource(null)
    resourceFormalApi.getSource(path).then(setSource).catch(() => setSource(null))
  }, [path])
  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} />
      <div className="w-full md:w-[72%] lg:w-[60%] bg-white border-l border-qcloud-border shadow-2xl flex flex-col">
        <div className="px-4 py-2.5 border-b border-qcloud-border flex items-center gap-3 flex-shrink-0">
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-qcloud-text truncate">{source?.module || '—'}</div>
            <div className="text-[11px] text-qcloud-muted font-mono truncate">{path}</div>
          </div>
          <button onClick={onClose} className="text-qcloud-muted hover:text-qcloud-text p-1" title="Close">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="flex-1 min-h-0 overflow-hidden">
          {!source ? (
            <div className="h-full flex items-center justify-center text-qcloud-muted">Loading…</div>
          ) : (
            <Editor
              height="100%"
              defaultLanguage="lean4"
              language="lean4"
              theme="vs"
              value={source.content}
              options={{
                readOnly: true,
                minimap: { enabled: true, scale: 1 },
                fontSize: 13,
                lineNumbers: 'on',
                wordWrap: 'on',
                automaticLayout: true,
                scrollBeyondLastLine: false,
              }}
              beforeMount={registerLean4}
              onMount={(editor) => { if (line) editor.revealLineInCenter(line) }}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default TutorialAdderPage
